import {
  IconCheck,
  IconCheckbox,
  IconCircleDashed,
  IconClock,
  IconUser,
} from "@tabler/icons-react";
import { Badge, Card, Group, Stack, Text, ThemeIcon, Timeline } from "@/components/ui";
import type { FlujoDeAutorizacion } from "@/lib/types";
import { formatFechaHora } from "@/shared/format/fecha";

/**
 * Card que visualiza el flujo de autorización de una solicitud · timeline
 * vertical con un nivel por bullet · marca autorizados (con quién y cuándo),
 * en proceso (desde cuándo) y pendientes.
 */
export function FlujoCard({
  flujo,
  fill = false,
}: {
  flujo: FlujoDeAutorizacion;
  /** Si true, la card crece para llenar la altura disponible de su columna. */
  fill?: boolean;
}) {
  const activeIndex = flujo.niveles.findIndex((n) => n.estado === "EnProceso");
  const autorizados = flujo.niveles.filter((n) => n.estado === "Autorizado").length;
  return (
    <Card padding="md" className={fill ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon variant="filled" color="multiva" radius="md" size="md">
              <IconCheckbox size={14} />
            </ThemeIcon>
            <Text fw={600} size="sm">
              Flujo de autorización
            </Text>
          </Group>
          <Badge size="xs" color="multiva">
            {autorizados}/{flujo.cantidadNiveles}
          </Badge>
        </Group>
        <Timeline active={activeIndex >= 0 ? activeIndex : autorizados - 1} bulletSize={22} color="multiva">
          {flujo.niveles.map((n) => {
            const isAutorizado = n.estado === "Autorizado";
            const isEnProceso = n.estado === "EnProceso";
            const color = isAutorizado ? "exito" : isEnProceso ? "multiva" : "slate";
            const autorizador =
              n.autorizadoPorNombre?.trim() ||
              n.autorizadoPorEmail ||
              (n.autorizadoPorUsuarioId
                ? `Usuario ${n.autorizadoPorUsuarioId.slice(0, 8)}…`
                : null);
            return (
              <Timeline.Item
                key={n.id}
                color={color}
                bullet={
                  isAutorizado ? (
                    <IconCheck size={12} stroke={2} />
                  ) : isEnProceso ? (
                    <IconCircleDashed size={12} />
                  ) : null
                }
                title={
                  <Group gap={8} align="center">
                    <Text size="sm" fw={600}>
                      Nivel {n.nivel}
                    </Text>
                    <Badge size="xs" color={color}>
                      {isAutorizado ? "Autorizado" : isEnProceso ? "En proceso" : "Pendiente"}
                    </Badge>
                  </Group>
                }
              >
                <Stack gap={4} className="mt-0.5">
                  <Text size="xs" c="dimmed">
                    {n.rolesAutorizadores.join(" · ")}
                  </Text>
                  {isAutorizado && autorizador ? (
                    <Group gap={6} wrap="nowrap" align="center" className="mt-1">
                      <ThemeIcon variant="light" color="exito" size="xs" radius="xl">
                        <IconUser size={10} stroke={1.8} />
                      </ThemeIcon>
                      <Text size="xs" fw={500}>
                        {autorizador}
                      </Text>
                    </Group>
                  ) : null}
                  {isAutorizado && n.autorizadoAt ? (
                    <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatFechaHora(n.autorizadoAt)}
                    </Text>
                  ) : null}
                  {isEnProceso && n.iniciadoAt ? (
                    <Group gap={6} wrap="nowrap" align="center" className="mt-1">
                      <ThemeIcon variant="light" color="multiva" size="xs" radius="xl">
                        <IconClock size={10} stroke={1.8} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                        Esperando desde {formatFechaHora(n.iniciadoAt)}
                      </Text>
                    </Group>
                  ) : null}
                  {!isAutorizado && !isEnProceso ? (
                    <Text size="xs" c="dimmed" className="mt-0.5 italic">
                      Esperando que avance el flujo
                    </Text>
                  ) : null}
                </Stack>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Stack>
    </Card>
  );
}
